'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import { useSession } from 'next-auth/react'

import { extractCustomerFromClientSession } from 'modules/customer'
import { ProductTypes } from 'modules/products'
import { SeoListName } from 'shared/analytics'
import Button from 'shared/ui/Button'
import IconButton from 'shared/ui/IconButton'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import { removeFromCartSeo } from '../../../analytics'
import useCartStore from '../../../store/cartStore'
import { CartItem } from '../../../types'

import SubstituteProductCard from './SubstituteProductCard'
import SubstitutesSlider from './SubstitutesSlider'

import styles from './UnavailableItemsResolver.module.scss'

interface UnavailableItemsResolverProps {
  categoryAgeLimitMet?: boolean
  handleClose: () => void
  productsNotAvailable: CartItem[]

  deliveryMethodId?: string | null
}
interface DimensionsSlider {
  width: number
  height: number
}

const UnavailableItemsResolver = ({
  handleClose,
  productsNotAvailable,
  deliveryMethodId
}: UnavailableItemsResolverProps) => {
  const session = useSession()
  const user = useMemo(() => extractCustomerFromClientSession(session), [session])
  const {
    cart,
    setProductReplacements,
    getProductReplacements,
    getProductCount,
    replaceProduct,
    removeFromCart,
    cartReplacementsPayload
  } = useCartStore()
  const sliderRef = useRef<HTMLDivElement>(null)
  const [dimensionsSlider, setDimensionsSlider] = useState<DimensionsSlider>({
    width: 0,
    height: 0
  })
  const [activeSliderPosition, setActiveSliderPosition] = useState<number>(0)
  const [activeSliderStep, setActiveSliderStep] = useState<number>(1)
  const [
    activeSliderPositionReplacements,
    setActiveSliderPositionReplacements
  ] = useState<number>(0)

  const isReplacementSelected = useMemo(
    () =>
      cartReplacementsPayload.some(item => isNotNilOrEmpty(item.substitutes)),
    [cartReplacementsPayload]
  )

  const deliveryDayAsNumber = cart?.deliverySlot?.date
    ? new Date(cart?.deliverySlot?.date || '').getDay()
    : null

  useEffect(() => {
    const getDimensionsSlider = () => {
      if (sliderRef.current) {
        setDimensionsSlider({
          width: sliderRef?.current?.offsetWidth,
          height: sliderRef.current.offsetHeight
        })
      }
    }

    getDimensionsSlider()

    window.addEventListener('resize', getDimensionsSlider)

    return () => {
      window.removeEventListener('resize', getDimensionsSlider)
    }
  }, [])

  useEffect(() => {
    if (productsNotAvailable) {
      const payload = productsNotAvailable.map(prod => ({
        productId: prod.product?.id || '',
        substitutes: []
      }))

      setProductReplacements(payload)
    }
  }, [
    productsNotAvailable,
    productsNotAvailable.length,
    setProductReplacements
  ])

  const scrollLeft = useCallback(() => {
    setActiveSliderPosition(prev => prev + dimensionsSlider.width)
    setActiveSliderPositionReplacements(prev => prev + dimensionsSlider.width)
    setActiveSliderStep(prev => prev - 1)
  }, [dimensionsSlider.width])

  const scrollRight = useCallback(() => {
    setActiveSliderPosition(prev => prev - dimensionsSlider.width)
    setActiveSliderPositionReplacements(prev => prev - dimensionsSlider.width)
    setActiveSliderStep(prev => prev + 1)
  }, [dimensionsSlider.width])

  const handleReplaceProduct = async () => {
    const items = getProductReplacements()
    const filtered = items.filter(item => isNotNilOrEmpty(item.substitutes))
    const needToSlide = activeSliderStep >= productsNotAvailable.length

    await replaceProduct(filtered, session)
      .then(() => {
        toast.success('Produkty zostały zamienione')
        if (filtered.length === productsNotAvailable.length) {
          handleClose()
        } else if (needToSlide) {
          scrollLeft()
        }
      })
      .catch(err => {
        toast.error(getApiErrors(err))
      })
  }

  const handleRemoveFromCartSeo = useCallback(
    (replacement: ProductTypes, idxOnList: number) => {
      removeFromCartSeo({
        user,
        product: replacement,
        button_place: 'Modal zamienników',
        idxOnList,
        productQuantity: getProductCount(replacement.id),
        cart,
        listName: SeoListName.REPLACEMENTS_LIST_NAME,
        type: 'remove'
      })
    },
    [user, cart, getProductCount]
  )

  const handleRemoveProduct = useCallback(
    async ({
      replacement,
      idxOnList,
      itemId
    }: {
      replacement: ProductTypes
      idxOnList: number
      itemId: string
    }) => {
      const isLastProductToSlide =
        idxOnList !== 0 && idxOnList === productsNotAvailable.length - 1

      await removeFromCart({ itemId }, session)
      handleRemoveFromCartSeo(replacement, idxOnList)

      if (isLastProductToSlide) {
        scrollLeft()
      }
    },
    [
      handleRemoveFromCartSeo,
      productsNotAvailable.length,
      removeFromCart,
      scrollLeft,
      session
    ]
  )

  return (
    <div id='replacementsWrapper' className={styles.wrapper}>
      <div className={styles.sliderWrapper}>
        <div
          id='Controls'
          className={styles.controls}
          style={
            {
              '--controls-top': `${dimensionsSlider.height / 2 - 20}px`
            } as React.CSSProperties
          }
        >
          <IconButton
            variant='gray'
            onClick={scrollLeft}
            size='medium'
            icon={<IconArrowLeft />}
            disabled={activeSliderStep === 1}
            aria-label='Przejdź do poprzedniego produktu'
          />
          <IconButton
            variant='gray'
            onClick={scrollRight}
            size='medium'
            icon={<IconArrowRight />}
            disabled={activeSliderStep === productsNotAvailable.length}
            aria-label='Przejdź do następnego produktu'
          />
        </div>
        <div className={styles.sliderContainer}>
          {/* carousel of unavailable products */}
          <div
            className={styles.sliderAbsolute}
            style={
              {
                '--slider-height': `${dimensionsSlider.height}px`
              } as React.CSSProperties
            }
          >
            <div
              className={styles.slider}
              style={
                {
                  '--slider-position': `${activeSliderPosition}px`
                } as React.CSSProperties
              }
              ref={sliderRef}
            >
              {productsNotAvailable.map((product: CartItem, idx: number) => {
                return (
                  <div className={styles.productWrapper} key={product.id}>
                    <SubstituteProductCard
                      itemId={product.id}
                      replacement={product.product}
                      idxOnList={idx}
                      onRemoveProduct={handleRemoveProduct}
                    />
                  </div>
                )
              })}
            </div>
          </div>
          {/* carousel of unavailable products */}
          {/*carousel of replacements */}
          <div className={styles.replacementsAbsolute}>
            <div
              className={styles.replacementsSliderWrapper}
              style={
                {
                  '--replacements-position': `${activeSliderPositionReplacements}px`
                } as React.CSSProperties
              }
            >
              {productsNotAvailable.map((item: CartItem) => {
                return (
                  <SubstitutesSlider
                    key={item.id}
                    product={item.product}
                    dimensionsSlider={dimensionsSlider}
                    deliveryDayAsNumber={deliveryDayAsNumber}
                    deliveryMethodId={deliveryMethodId}
                  />
                )
              })}
            </div>
          </div>
          {/*carousel of replacements */}
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          variant='primary'
          onClick={handleReplaceProduct}
          disabled={!isReplacementSelected}
        >
          Zapisz zmiany
        </Button>
        <Button variant='neutralOutline' onClick={handleClose}>
          Anuluj
        </Button>
      </div>
    </div>
  )
}

export default UnavailableItemsResolver
